import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { catchError, finalize, forkJoin, of } from "rxjs";
import { ToastrService } from "ngx-toastr";

import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import { PermissionDto, PermissionTypeEnum, UserDto, UserPermissionPayloadDto } from "../../../dto";
import { HttpUserService } from "../../../services/user/http-user.service";

@Component({
    selector: 'app-permissions-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NzModalModule,
        NzButtonModule,
        NzSpinModule,
        NzCardModule,
        NzIconModule,
        NzTooltipModule,
        NzCheckboxModule
    ],
    templateUrl: './permissions-modal.component.html',
    styleUrls: ['./permissions-modal.component.scss']
})
export class PermissionsModalComponent {
    @Input() modalId = 'permissionsModal';

    user: UserDto | undefined;
    allPermissions: PermissionDto[] = [];
    selectedIds: Set<number> = new Set();
    loading = false;
    saving = false;

    isVisible = false;
    PermissionTypeEnum = PermissionTypeEnum;

    constructor(
        private httpService: HttpUserService,
        private toastr: ToastrService
    ) {}

    loadPermissions(): void {
        if (!this.user || !this.user.id){ 
            return;
        }
        this.loading = true;

        // Use cached allPermissions if available, otherwise fetch from API
        const allPerms$ = this.allPermissions.length > 0 
            ? of(this.allPermissions) 
            : this.httpService.getAllPermissions();
            
        // Fetch fresh user permissions if user has an ID, otherwise fallback to the DTO array
        const userPerms$ = this.httpService.getUserPermissions(this.user.id);

        forkJoin({
            all: allPerms$,
            user: userPerms$
        })
        .pipe(
            catchError(err => {
                this.toastr.error('Failed to load permissions');
                throw err;
            }),
            finalize(() => this.loading = false)
        )
        .subscribe(({ all, user }) => {
            this.allPermissions = all ?? [];
            
            this.selectedIds = new Set(
                (user ?? [])
                    .map(p => p.id)
                    .filter((id): id is number => id != null)
            );
        });
    }

    open(user: UserDto): void {
        this.user = user;
        this.isVisible = true;
        this.loadPermissions();
    }

    permissionsByType(type: PermissionTypeEnum): PermissionDto[] {
        return this.allPermissions.filter(p => p.type === type);
    }

    isChecked(perm: PermissionDto): boolean {
        return perm.id != null && this.selectedIds.has(perm.id);
    }

    toggle(perm: PermissionDto): void {
        if (perm.id == null) return;
        if (this.selectedIds.has(perm.id)) {
            this.selectedIds.delete(perm.id);
        } else {
            this.selectedIds.add(perm.id);
        }
    }

    selectAllForType(type: PermissionTypeEnum): void {
        this.permissionsByType(type).forEach(p => { if (p.id != null) this.selectedIds.add(p.id); });
    }

    clearAllForType(type: PermissionTypeEnum): void {
        this.permissionsByType(type).forEach(p => { if (p.id != null) this.selectedIds.delete(p.id); });
    }

    close(): void {
        this.isVisible = false;
    }

    save(): void {
        if (!this.user?.id) return;
        this.saving = true;
        
        const permissionIds = Array.from(this.selectedIds);        
        this.httpService.updateUserPermissions(this.user.id, permissionIds)
            .pipe(
                catchError(err => {
                    this.toastr.error('Failed to update permissions');
                    throw err;
                }),
                finalize(() => this.saving = false)
            )
            .subscribe(resp => {
                if (resp.success !== false) {
                    this.toastr.success('Permissions updated.');
                    this.close();
                } else {
                    this.toastr.error(resp.error ?? 'Failed to update permissions');
                }
            });
    }

    getCategoriesByType(type: PermissionTypeEnum): string[] {
        const categories = this.allPermissions
            .filter(p => p.type === type)
            .map(p => p.category ?? 'General'); 
        
        return [...new Set(categories)].sort();
    }

    getPermissionsByGroup(type: PermissionTypeEnum, category: string): PermissionDto[] {
        return this.allPermissions.filter(p => 
            p.type === type && (p.category ?? 'General') === category
        );
    }

    selectAllForCategory(type: PermissionTypeEnum, category: string): void {
        this.getPermissionsByGroup(type, category).forEach(p => {
            if (p.id != null) this.selectedIds.add(p.id);
        });
    }
}